import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../api/api-config';
import { endpoints } from '../api/endPoints';
import {
  getAccessTokenCookie,
  getLastActivityCookie,
  getRefreshTokenCookie,
  removeAuthCookies,
  removeLastActivityCookie,
  removeUserCookies,
  setLastActivityCookie,
  setAuthCookies,
} from './Cookie-utils';

/** Idle timeout: logout after this many ms without user activity */
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

/** How often to check whether idle limit has been reached */
export const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

/** Refresh access token when it expires within this window and user is still active */
const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;

const ACTIVITY_EVENTS = [
  'click',
  'keydown',
  'keypress',
  'mousedown',
  'mousemove',
  'scroll',
  'touchstart',
  'submit',
  'focusin',
  'popstate',
];

let idleCheckIntervalId = null;
let activityListenersAttached = false;
let isLoggingOut = false;
let lastMouseMoveRecordedAt = 0;
let refreshPromise = null;

export const getAccessToken = () => getAccessTokenCookie();

export const getRefreshToken = () => getRefreshTokenCookie();

export const isAccessTokenExpired = (token = getAccessToken()) => {
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    if (!exp) return true;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

export const getAccessTokenExpiresInMs = (token = getAccessToken()) => {
  if (!token) return 0;

  try {
    const { exp } = jwtDecode(token);
    if (!exp) return 0;
    return Math.max(0, exp * 1000 - Date.now());
  } catch {
    return 0;
  }
};

export const shouldRefreshAccessToken = (token = getAccessToken()) => {
  if (!token) return false;
  return getAccessTokenExpiresInMs(token) <= TOKEN_REFRESH_BUFFER_MS;
};

export const getLastActivityTime = () => {
  const stored = getLastActivityCookie();
  if (stored) {
    const parsed = Number(stored);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
};

export const getIdleTimeMs = () => Math.max(0, Date.now() - getLastActivityTime());

export const isIdleTimeoutExceeded = () => getIdleTimeMs() >= IDLE_TIMEOUT_MS;

export const recordUserActivity = () => {
  if (!getAccessToken()) return;
  setLastActivityCookie(Date.now());
};

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await fetch(`${API_BASE_URL}${endpoints.authentication.refreshToken}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const payload = await response.json();
    const newToken = payload?.data?.token;

    if (!newToken) {
      throw new Error('No access token returned from refresh');
    }

    setAuthCookies({ accessToken: newToken });
    return newToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

/**
 * Keep session alive while user is active: refresh JWT before it expires.
 * Only idle timeout should end the session for active users.
 */
export const ensureValidAccessToken = async () => {
  if (isIdleTimeoutExceeded()) {
    logoutToLogin({
      message: 'You have been logged out due to 15 minutes of inactivity.',
    });
    throw new Error('Session expired due to inactivity');
  }

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token');
  }

  if (!isAccessTokenExpired(token) && !shouldRefreshAccessToken(token)) {
    return token;
  }

  return refreshAccessToken();
};

const maybeRefreshOnActivity = () => {
  if (isIdleTimeoutExceeded() || !getAccessToken()) return;
  if (shouldRefreshAccessToken() || isAccessTokenExpired()) {
    refreshAccessToken().catch(() => {
      // Ignore background refresh errors; the next API call will retry.
    });
  }
};

const handleActivity = (event) => {
  if (event.type === 'mousemove') {
    const now = Date.now();
    if (now - lastMouseMoveRecordedAt < 1000) return;
    lastMouseMoveRecordedAt = now;
  }
  recordUserActivity();
  maybeRefreshOnActivity();
};

const attachActivityListeners = () => {
  if (activityListenersAttached) return;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleActivity, { capture: true, passive: true });
  });

  activityListenersAttached = true;
};

const detachActivityListeners = () => {
  if (!activityListenersAttached) return;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleActivity, { capture: true });
  });

  activityListenersAttached = false;
};

const checkIdleTimeout = () => {
  if (!getAccessToken()) {
    clearSessionTimeoutMonitor();
    return;
  }

  if (isIdleTimeoutExceeded()) {
    logoutToLogin({
      message: 'You have been logged out due to 15 minutes of inactivity.',
    });
    return;
  }

  maybeRefreshOnActivity();
};

export const clearSessionTimeoutMonitor = () => {
  if (idleCheckIntervalId) {
    clearInterval(idleCheckIntervalId);
    idleCheckIntervalId = null;
  }
  detachActivityListeners();
};

export const clearSessionStorage = () => {
  removeAuthCookies();
  removeLastActivityCookie();
  removeUserCookies();
};

export const logoutToLogin = ({ message } = {}) => {
  if (isLoggingOut) return;

  const path = window.location.pathname || '';
  const isLoginPage = path === '/' || path.toLowerCase().includes('login');

  if (isLoginPage && !getAccessToken()) {
    return;
  }

  isLoggingOut = true;
  clearSessionTimeoutMonitor();
  clearSessionStorage();

  toast.error(message || 'Session expired. Please login again.');

  if (!isLoginPage) {
    window.location.assign('/');
  } else {
    isLoggingOut = false;
  }
};

export const startSessionTimeoutMonitor = ({ resetActivity = false } = {}) => {
  clearSessionTimeoutMonitor();

  const token = getAccessToken();
  if (!token) return;

  if (isIdleTimeoutExceeded()) {
    logoutToLogin({
      message: 'You have been logged out due to 15 minutes of inactivity.',
    });
    return;
  }

  if (resetActivity || !getLastActivityCookie()) {
    recordUserActivity();
  }

  attachActivityListeners();

  if (isAccessTokenExpired(token) || shouldRefreshAccessToken(token)) {
    refreshAccessToken().catch(() => {
      // Next API request will attempt refresh again.
    });
  }

  idleCheckIntervalId = setInterval(checkIdleTimeout, IDLE_CHECK_INTERVAL_MS);
  checkIdleTimeout();
};
